// "[verify]" in the manual note editor (v3.2 app): the flagged value is
// highlighted as in the read view (VERIFY_FLAG_CLASS) and the marker shows
// as a small "verify" chip that cannot be typed into (deleting it confirms
// the value, as Accept does). Both are decorations only: before the editor
// turns its HTML back into Markdown they are unwrapped, so the saved
// Markdown is exactly what the plain editor saves.

import { extractVerifyFlags, VERIFY_FLAG_CLASS } from "./verifyFlags";
import { verifyMarkerPattern } from "./verifyMarkers";

export const VERIFY_VALUE_ATTRIBUTE = "data-verify-value";
export const VERIFY_MARKER_ATTRIBUTE = "data-verify-marker";

// The marker text stays in the DOM (font-size 0) so Markdown, selection
// (selectVerifyMarker) and Undo keep working; the chip text comes from CSS.
// Kept as a literal so Tailwind generates the classes.
export const VERIFY_MARKER_CHIP_CLASS =
  "verify-marker mx-0.5 inline-block select-none rounded-full bg-v3-warn-tint align-baseline text-[0px] leading-none after:px-1.5 after:text-[11px] after:font-semibold after:leading-[18px] after:text-v3-warn-text after:content-[attr(data-label)]";

// The Markdown with each flagged value wrapped in a <mark>, markers kept.
export const markVerifyValuesForEditor = (markdown: string): string => {
  const flags = extractVerifyFlags(markdown);
  let out = "";
  let last = 0;
  for (const flag of flags) {
    // Values that are empty or overlap a previous one stay as they are.
    if (flag.valueEnd <= flag.valueStart || flag.valueStart < last) continue;
    out += `${markdown.slice(last, flag.valueStart)}<mark ${VERIFY_VALUE_ATTRIBUTE}="${flag.index}">${flag.rawValue}</mark>`;
    last = flag.valueEnd;
  }
  return out + markdown.slice(last);
};

// Styles the value marks and wraps each marker in a non-editable chip.
export const decorateVerifyInEditor = (
  root: HTMLElement,
  { label, title }: { label: string; title: string },
): void => {
  const doc = root.ownerDocument;
  for (const mark of root.querySelectorAll<HTMLElement>(
    `mark[${VERIFY_VALUE_ATTRIBUTE}]`,
  )) {
    mark.className = VERIFY_FLAG_CLASS;
    mark.title = title;
  }
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (text.parentElement?.closest(`code, pre, [${VERIFY_MARKER_ATTRIBUTE}]`))
      continue;
    if (verifyMarkerPattern().test(text.data)) texts.push(text);
  }
  for (const text of texts) {
    const fragment = doc.createDocumentFragment();
    let last = 0;
    for (const match of text.data.matchAll(verifyMarkerPattern())) {
      const start = match.index ?? 0;
      fragment.append(text.data.slice(last, start));
      const chip = doc.createElement("span");
      chip.setAttribute(VERIFY_MARKER_ATTRIBUTE, "");
      chip.setAttribute("contenteditable", "false");
      chip.setAttribute("data-label", label);
      chip.className = VERIFY_MARKER_CHIP_CLASS;
      chip.title = title;
      chip.textContent = match[0];
      fragment.append(chip);
      last = start + match[0].length;
    }
    fragment.append(text.data.slice(last));
    text.replaceWith(fragment);
  }
};

// The editor HTML without the decorations (their text kept in place).
export const unwrapVerifyDecorations = (html: string): string => {
  if (
    !html.includes(VERIFY_VALUE_ATTRIBUTE) &&
    !html.includes(VERIFY_MARKER_ATTRIBUTE)
  )
    return html;
  const template = document.createElement("template");
  template.innerHTML = html;
  for (const element of template.content.querySelectorAll(
    `[${VERIFY_VALUE_ATTRIBUTE}], [${VERIFY_MARKER_ATTRIBUTE}]`,
  ))
    element.replaceWith(...element.childNodes);
  template.content.normalize();
  return template.innerHTML;
};
